import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Camera, Code2, Download, Palette, ScanLine, ShieldCheck } from 'lucide-react'
import Logo from '@/components/app/logo'

interface AuthLayoutProps {
    title: string
    subtitle: string
    children: ReactNode
    footer: ReactNode
}

const FEATURES = [
    { icon: ScanLine, title: 'Real browser analysis', text: 'Pages are opened in an isolated remote Chromium session.' },
    { icon: Camera, title: 'Multi-device captures', text: 'Desktop, tablet, mobile and full-page screenshots plus recordings.' },
    { icon: Palette, title: 'Design system extraction', text: 'Colors, typography, spacing, components and motion.' },
    { icon: Code2, title: 'Editable React rebuild', text: 'Vite, TypeScript, Tailwind, Framer Motion and Lucide.' },
    { icon: Download, title: 'Download and run', text: 'A validated ZIP that installs and runs with npm.' }
]

const AuthLayout: FC<AuthLayoutProps> = ({ title, subtitle, children, footer }): ReactNode => {
    return (
        <div className='relative grid min-h-screen lg:grid-cols-[1.1fr_1fr]'>
            <div className='pointer-events-none absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_top_left,black_25%,transparent_70%)]' aria-hidden='true' />

            <section aria-label='About WebReplica AI' className='relative hidden flex-col justify-between overflow-hidden border-r border-border p-10 lg:flex xl:p-14'>
                <div className='absolute -left-24 top-24 size-[420px] rounded-full bg-brand-violet/25 blur-[120px]' aria-hidden='true' />
                <div className='absolute bottom-0 right-0 size-[360px] rounded-full bg-brand-blue/20 blur-[120px]' aria-hidden='true' />

                <Link href='/' className='relative w-fit'>
                    <Logo />
                </Link>

                <div className='relative max-w-xl'>
                    <p className='inline-flex items-center gap-2 rounded-full border border-brand-violet/30 bg-brand-violet/10 px-3 py-1 text-xs font-medium text-violet-200'>
                        <span className='size-1.5 animate-glow rounded-full bg-brand-cyan' />
                        AI website analyzer and rebuilder
                    </p>
                    <h1 className='mt-6 text-4xl font-semibold leading-[1.1] text-white xl:text-5xl'>
                        Turn any public website into an <span className='text-gradient'>editable React project</span>.
                    </h1>
                    <p className='mt-5 max-w-lg text-base leading-relaxed text-muted-foreground'>
                        Paste a URL. WebReplica captures every page in a real browser, maps its design system and rebuilds it as clean, downloadable code you can refine in plain language.
                    </p>

                    <ul className='mt-10 grid gap-3'>
                        {FEATURES.map(feature => {
                            const Icon = feature.icon

                            return (
                                <li key={feature.title} className='glass flex items-start gap-4 rounded-2xl p-4'>
                                    <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/[0.04] ring-1 ring-white/10'>
                                        <Icon className='size-5 text-violet-300' />
                                    </span>
                                    <span>
                                        <span className='block text-sm font-semibold text-white'>{feature.title}</span>
                                        <span className='block text-sm text-muted-foreground'>{feature.text}</span>
                                    </span>
                                </li>
                            )
                        })}
                    </ul>
                </div>

                <p className='relative flex items-center gap-2 text-xs text-muted-foreground'>
                    <ShieldCheck className='size-4 text-emerald-400' />
                    Public pages only. Respects robots.txt, logins, paywalls and CAPTCHAs.
                </p>
            </section>

            <section className='relative flex items-center justify-center px-5 py-12 sm:px-8'>
                <div className='w-full max-w-md'>
                    <div className='mb-8 lg:hidden'>
                        <Logo />
                    </div>

                    <div className='glass-strong ring-gradient rounded-3xl p-7 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] sm:p-9'>
                        <h2 className='text-2xl font-semibold text-white'>{title}</h2>
                        <p className='mt-2 text-sm text-muted-foreground'>{subtitle}</p>

                        <div className='mt-8'>
                            {children}
                        </div>
                    </div>

                    <div className='mt-6 text-center text-sm text-muted-foreground'>
                        {footer}
                    </div>
                </div>
            </section>
        </div>
    )
}

export default AuthLayout
